import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldCheck, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BAA-counter strip shown at the top of /trust.
 *
 * Live freshness signal — fetches /api/trust/baa-stats once on mount.
 * The endpoint is public (no auth) so unauthenticated procurement
 * scrapers can see the live count.
 */
export const BaaCounterStrip = () => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/trust/baa-stats`, { timeout: 8000 });
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return null;

  const items = stats
    ? [
        { label: 'Subprocessors', value: stats.total_subprocessors },
        { label: 'BAAs signed',   value: stats.baa_signed },
        { label: 'PHI-touching',  value: stats.phi_touching },
      ]
    : [
        { label: 'Subprocessors', value: '…' },
        { label: 'BAAs signed',   value: '…' },
        { label: 'PHI-touching',  value: '…' },
      ];

  const reviewed = stats?.last_reviewed_at
    ? new Date(stats.last_reviewed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  return (
    <div
      className="border-y border-cn-border bg-cn-bone-100/40 dark:bg-cn-ink-800/30"
      data-testid="baa-counter-strip"
    >
      <div className="cn-container max-w-5xl py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
        <div className="flex items-center gap-2 text-cn-primary">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">Live BAA chain</span>
        </div>
        <div className="flex items-center gap-5 flex-1">
          {items.map((it, i) => (
            <div key={it.label} data-testid={`baa-stat-${i}`}>
              <div className="font-display font-bold text-xl text-cn-text dark:text-white" data-testid={`baa-stat-${i}-value`}>
                {it.value}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{it.label}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-cn-text-muted flex items-center gap-1.5" data-testid="baa-last-reviewed">
          <RefreshCw className="w-3 h-3" />
          Last reviewed {reviewed}
        </div>
      </div>
    </div>
  );
};

export default BaaCounterStrip;
